import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarClock, Clock, MapPin, ArrowLeft, ArrowRight, Flame, MessageCircle, Link2, Check } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import { API } from "@/lib/faro";
import { cleanCourse, coursesFromSchedule, mergeCourses, seatsLabel, PUNTOS_EUR, CARNET_EUR } from "@/lib/courses";
import { useSeo, JsonLd, SITE_URL } from "@/lib/seo";
import { CoursesItemListJsonLd } from "@/components/CourseSchemaJsonLd";
import scheduleSeed from "@/data/schedule.generated.json";

// Convocatorias pre-renderizadas desde el JSON del build (misma forma que /api/courses/schedule)
function seedFromGenerated(gen) {
  const c = gen && gen.convocatorias;
  if (!c) return null;
  const norm = (arr) => (arr || []).map((x) => ({ start: x.startDay || x.start, end: x.endDay || x.end }));
  return { A: norm(c.A), B1: norm(c.B1) };
}
const SEED_COURSES = coursesFromSchedule(seedFromGenerated(scheduleSeed));

const CALENDAR_FAQ = [
  { q: "¿Con cuánta antelación debo reservar mi plaza?", a: "Recomendamos reservar al menos 2 semanas antes de la convocatoria: los grupos son reducidos y las plazas del curso de puntos suelen agotarse. Si tu plazo de la DGT aprieta, llámanos al 986 29 24 55 y buscamos hueco." },
  { q: "¿Qué pasa si reservo y no puedo asistir?", a: "Avísanos cuanto antes y te pasamos a la siguiente convocatoria sin coste. La asistencia es obligatoria para que la DGT dé por aprovechado el curso, así que es mejor mover la fecha que faltar a una sesión." },
  { q: "¿Abrís convocatorias extra si se llena un grupo?", a: "Sí. Cuando una convocatoria se completa con lista de espera, abrimos una fecha adicional ese mismo mes. Déjanos tus datos en el formulario y te avisamos en cuanto se confirme." },
  { q: "¿Hasta cuándo puedo apuntarme a una convocatoria?", a: "Hasta 2 días laborables antes del inicio, siempre que queden plazas y dé tiempo a tramitar tu documentación con la DGT. El ciclo adicional es aparte: al ser individual, lo programamos de un día para otro." },
];

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAYS_ES = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

function parseIso(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatMonthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(d) {
  return `${MONTHS_ES[d.getMonth()]} ${d.getFullYear()}`;
}

function formatDayLabel(d) {
  return WEEKDAYS_ES[d.getDay()];
}

function formatTime(d) {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}h`;
}

function daysUntil(d) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfTarget = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfTarget - startOfToday) / (1000 * 60 * 60 * 24));
}

function startLabel(d) {
  if (d === 0) return "Empieza hoy";
  if (d === 1) return "Empieza mañana";
  return `En ${d} días`;
}

function CourseCard({ course, date }) {
  const [copied, setCopied] = useState(false);
  const seatsLow = typeof course.seats_remaining === "number" && course.seats_remaining <= 5;
  const showSeats = typeof course.seats_remaining === "number" && course.seats_remaining >= 0;
  const d = daysUntil(date);
  const dLabel = startLabel(d);
  const isCarnet20h = /carnet|20\s*h/i.test(course.course_label || "");
  const ctaHref = isCarnet20h
    ? "/curso-sensibilizacion-vigo#reserva"
    : "/recuperacion-de-puntos-vigo#puntos-formulario";

  const shareText = `📅 Próximo curso en Autoescuelas Faro: ${course.course_label}\n${course.subtitle || ""}\nEmpieza el ${date.getDate()} de ${MONTHS_ES[date.getMonth()]} de ${date.getFullYear()} a las ${formatTime(date)}\n${window.location.origin}/proximos-cursos?utm_source=whatsapp&utm_medium=social&utm_campaign=share_boton`;
  const waHref = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      // fallback: legacy
      const ta = document.createElement("textarea");
      ta.value = shareText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <div className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5" data-testid={`upcoming-card-${course.course_label}`}>
      {/* Date block */}
      <div className="flex sm:flex-col items-center sm:items-center gap-3 sm:gap-1 sm:w-24 sm:shrink-0 sm:border-r sm:border-[rgba(30,42,143,0.12)] sm:pr-5">
        <div className="text-[10px] uppercase tracking-[0.22em] font-black text-[var(--faro-red)] sm:order-1">
          {formatDayLabel(date)}
        </div>
        <div className="font-display font-black text-4xl leading-none tabular-nums text-[var(--faro-blue-deep)] sm:order-2">
          {String(date.getDate()).padStart(2, "0")}
        </div>
        <div className="text-xs font-bold text-[var(--faro-text-soft)] sm:order-3">
          {MONTHS_ES[date.getMonth()].slice(0, 3).toLowerCase()}. {date.getFullYear()}
        </div>
      </div>

      {/* Course info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.15em] text-[var(--faro-blue)]">
          <CalendarClock size={13} /> {dLabel}
        </div>
        <h3 className="font-display font-black text-xl sm:text-2xl mt-1 leading-tight">{course.course_label}</h3>
        {course.subtitle && (
          <p className="text-sm text-[var(--faro-text-soft)] mt-1">{course.subtitle}</p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[var(--faro-text-soft)]">
          <span className="inline-flex items-center gap-1.5">
            <Clock size={14} className="text-[var(--faro-blue)]" /> {formatTime(date)}
          </span>
          <span className="inline-flex items-center gap-1">
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              data-testid={`upcoming-share-wa-${course.course_label}`}
              title="Compartir por WhatsApp"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold text-white bg-[#25d366] hover:brightness-95 transition-all"
            >
              <MessageCircle size={12} /> WhatsApp
            </a>
            <button
              type="button"
              onClick={copyLink}
              data-testid={`upcoming-share-copy-${course.course_label}`}
              title="Copiar enlace"
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold transition-all ${
                copied
                  ? "bg-green-600 text-white"
                  : "bg-[var(--faro-bg)] text-[var(--faro-blue)] hover:bg-[var(--faro-blue)] hover:text-white"
              }`}
            >
              {copied ? <><Check size={12} /> Copiado</> : <><Link2 size={12} /> Copiar</>}
            </button>
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col items-stretch gap-2 sm:w-52 sm:shrink-0">
        {showSeats && (
          <div className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider ${
            seatsLow ? "bg-[var(--faro-red)] text-white" : "bg-[var(--faro-blue-deep)] text-white"
          }`}>
            {seatsLow && <Flame size={12} />}
            {seatsLabel(course.seats_remaining)}
          </div>
        )}
        <a
          href={ctaHref}
          data-testid={`upcoming-cta-${course.course_label}`}
          className="btn-pill btn-primary justify-center text-xs"
        >
          {course.cta_label || "Reservar mi plaza"} <ArrowRight size={13} />
        </a>
      </div>
    </div>
  );
}

export default function UpcomingCoursesPage() {
  const [courses, setCourses] = useState(SEED_COURSES);
  const [loading, setLoading] = useState(SEED_COURSES.length === 0);

  useSeo({
    title: "Próximos cursos en Vigo 2026: fechas y plazas · Faro",
    description: "Calendario oficial de Autoescuelas Faro: fechas confirmadas de los cursos de recuperación de puntos y del carnet, CAP e intensivos en Vigo. Plazas limitadas, reserva online.",
    canonical: `${SITE_URL}/proximos-cursos`,
    ogImage: "/assets/og/proximos-cursos-vigo.jpg",
    ogImageAlt: "Calendario de próximos cursos de recuperación de puntos en Vigo",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    Promise.all([
      axios.get(`${API}/next-course`).catch(() => ({ data: {} })),
      axios.get(`${API}/courses/schedule`).catch(() => ({ data: null })),
    ]).then(([nc, sched]) => {
      const admin = (Array.isArray(nc.data.courses) ? nc.data.courses : []).map(cleanCourse);
      setCourses(mergeCourses(admin, coursesFromSchedule(sched.data || seedFromGenerated(scheduleSeed))));
    }).finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const upcoming = courses
      .map((c) => ({ ...c, _date: parseIso(c.start_date) }))
      .filter((c) => c._date && c._date.getTime() >= startOfToday)
      .sort((a, b) => a._date - b._date);

    const map = new Map();
    for (const c of upcoming) {
      const key = formatMonthKey(c._date);
      if (!map.has(key)) map.set(key, { label: formatMonthLabel(c._date), items: [] });
      map.get(key).items.push(c);
    }
    return Array.from(map.entries()).map(([key, val]) => ({ key, ...val }));
  }, [courses]);

  const total = grouped.reduce((n, g) => n + g.items.length, 0);

  return (
    <div data-testid="upcoming-courses-page" className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0">
      <CoursesItemListJsonLd />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: CALENDAR_FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }} />
      <Header />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-1">
        <Link
          to="/"
          data-testid="upcoming-back"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--faro-blue)] hover:text-[var(--faro-red)] transition-colors"
        >
          <ArrowLeft size={16} /> Volver al inicio
        </Link>
      </div>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <span className="eyebrow">Agenda de cursos</span>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
            Calendario de próximos cursos en Vigo
          </h1>
          <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
            Esta es la página oficial del calendario de Autoescuelas Faro: todas las <strong>convocatorias confirmadas</strong> del <Link to="/recuperacion-de-puntos-vigo">curso de recuperación de puntos en Vigo (215 €)</Link>, del <Link to="/curso-sensibilizacion-vigo">curso de recuperación del carnet (20 h)</Link> y del <Link to="/curso-cap-vigo">CAP</Link>. Organizamos
            <strong> una convocatoria al mes</strong> en nuestra aula de Plaza América, normalmente en <strong>viernes por la tarde</strong>,
            con <strong>plazas limitadas</strong> por grupo. Reserva desde el formulario de cada curso, por WhatsApp
            (637 570 075) o llamando al 986 29 24 55.
          </p>
        </div>

        {loading && (
          <div className="text-[var(--faro-text-soft)]" data-testid="upcoming-loading">Cargando agenda...</div>
        )}

        {!loading && total === 0 && (
          <div className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-8 text-center" data-testid="upcoming-empty">
            <CalendarClock size={40} className="mx-auto text-[var(--faro-blue)] mb-3" />
            <h3 className="font-display font-black text-xl">No hay cursos programados por ahora</h3>
            <p className="text-[var(--faro-text-soft)] mt-2">
              Estamos preparando las próximas fechas. Contacta con nosotros para más información o vuelve pronto.
            </p>
            <a href="/#contacto" className="btn-pill btn-primary mt-5 inline-flex">
              <MapPin size={15} /> Contactar
            </a>
          </div>
        )}

        <div className="space-y-10">
          {grouped.map((group) => (
            <section key={group.key} data-testid={`upcoming-month-${group.key}`}>
              <div className="flex items-baseline gap-3 mb-4">
                <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--faro-blue-deep)]">
                  {group.label}
                </h2>
                <span className="text-xs font-bold text-[var(--faro-text-soft)] uppercase tracking-wider">
                  {group.items.length} curso{group.items.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="space-y-3">
                {group.items.map((c, i) => (
                  <CourseCard key={`${group.key}-${i}`} course={c} date={c._date} />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border-2 border-dashed border-[rgba(30,42,143,0.25)] bg-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="ciclo-adicional-note">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--faro-red)]">Sin esperar convocatoria</div>
            <h3 className="font-display font-black text-xl mt-1">¿Necesitas el ciclo adicional (4 h)?</h3>
            <p className="text-sm text-[var(--faro-text-soft)] mt-1">
              El <strong>ciclo adicional</strong> no aparece en este calendario porque es <strong>individual y totalmente a demanda</strong>:
              se programa <strong>de un día para otro</strong>, presencial u online, y tú decides cuándo empiezas.
            </p>
          </div>
          <Link to="/ciclo-adicional-vigo" className="btn-pill btn-outline text-sm justify-center shrink-0" data-testid="ciclo-adicional-note-link">
            Más información <ArrowRight size={14} />
          </Link>
        </div>

        <section className="mt-12" data-testid="upcoming-comparativa">
          <h2 className="font-display font-black text-2xl sm:text-3xl">¿Qué curso necesitas? Comparativa rápida</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-[rgba(30,42,143,0.12)]">
            <table className="w-full text-sm bg-white">
              <thead className="bg-[var(--faro-blue)] text-white text-left">
                <tr>
                  <th className="px-4 py-3">Curso</th>
                  <th className="px-4 py-3">Para quién</th>
                  <th className="px-4 py-3">Duración</th>
                  <th className="px-4 py-3">¿Examen?</th>
                  <th className="px-4 py-3">Precio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(30,42,143,0.08)] text-[var(--faro-text-soft)]">
                <tr>
                  <td className="px-4 py-3 font-bold text-[var(--faro-blue-deep)]"><Link to="/recuperacion-de-puntos-vigo" className="underline underline-offset-2">Tipo A — Recuperación de puntos en Vigo</Link></td>
                  <td className="px-4 py-3">Conservas el carnet, recuperas hasta 4 puntos</td>
                  <td className="px-4 py-3 whitespace-nowrap">10 h</td>
                  <td className="px-4 py-3">No</td>
                  <td className="px-4 py-3 font-black text-[var(--faro-blue-deep)] whitespace-nowrap">{PUNTOS_EUR}</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-bold text-[var(--faro-blue-deep)]"><Link to="/curso-sensibilizacion-vigo" className="underline underline-offset-2">Tipo B1 — Recuperar el carnet en Vigo</Link></td>
                  <td className="px-4 py-3">Te quedaste a 0 puntos (pérdida de vigencia)</td>
                  <td className="px-4 py-3 whitespace-nowrap">20 h</td>
                  <td className="px-4 py-3">Sí, en Jefatura</td>
                  <td className="px-4 py-3 font-black text-[var(--faro-blue-deep)] whitespace-nowrap">{CARNET_EUR}</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-bold text-[var(--faro-blue-deep)]"><Link to="/curso-sensibilizacion-vigo" className="underline underline-offset-2">Tipos B2 y B3 — Sensibilización (judicial)</Link></td>
                  <td className="px-4 py-3">Retirada por sentencia (B2 ≤2 años · B3 &gt;2 años)</td>
                  <td className="px-4 py-3 whitespace-nowrap">20 h</td>
                  <td className="px-4 py-3">B2 no · B3 pruebas DGT</td>
                  <td className="px-4 py-3 font-black text-[var(--faro-blue-deep)] whitespace-nowrap">{CARNET_EUR}</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-bold text-[var(--faro-blue-deep)]"><Link to="/ciclo-adicional-vigo" className="underline underline-offset-2">Ciclo adicional</Link></td>
                  <td className="px-4 py-3">Suspendiste la prueba de Tráfico tras el B1</td>
                  <td className="px-4 py-3 whitespace-nowrap">4 h</td>
                  <td className="px-4 py-3">Repites la prueba</td>
                  <td className="px-4 py-3 whitespace-nowrap">A consultar</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-10" data-testid="upcoming-faq">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Preguntas sobre fechas y reservas</h2>
          <div className="mt-4 space-y-2">
            {CALENDAR_FAQ.map((f) => (
              <details key={f.q} className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] px-5 py-4">
                <summary className="cursor-pointer list-none font-bold text-[var(--faro-blue-deep)]">{f.q}</summary>
                <p className="mt-2 text-sm text-[var(--faro-text-soft)]">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
